/**
 * Reusable Levi Working Environment.
 *
 * Presentation-only container that houses Levi's contractor-facing
 * intelligence in a consistent, reusable layout. It owns:
 *   - the identity header
 *   - the calm status caption + a single aria-live region
 *   - a bounded, replaceable visual region
 *   - the layout order of the interaction / smart-redirect /
 *     reasoning / recommendation regions
 *
 * It does NOT own:
 *   - business logic
 *   - backend I/O
 *   - the seven-key recommendation contract
 *   - the four-key reasoning contract
 *   - action handlers
 *
 * The recommendation and reasoning surfaces are supplied by the
 * host page as region children — the working environment never
 * renders those contract keys itself. This is the Option B
 * composition boundary chosen during Slice 2F2 planning so that
 * duplicate renderers of the locked contracts are impossible by
 * construction.
 *
 * The visual region receives whatever the host page passes through
 * the `visualRegion` prop. The working environment does not
 * fabricate a placeholder asset, SVG system, or transparent asset;
 * if the page passes nothing, the visual slot is simply empty.
 * Future artwork can be swapped in without touching this file.
 */
import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/context/LangContext";
import { deriveLeviState, statusCaptionKeyFor, LeviState } from "./leviWorkingEnvironmentState";

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(!!mq.matches);
    apply();
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);
  return reduced;
};

const stateToVisualToken = (state) => {
  // Opaque tokens — deliberately not consumed by the default visual
  // implementation. Future visual implementations may branch on them
  // without any change to this file.
  switch (state) {
    case LeviState.LISTENING: return "settled_listening";
    case LeviState.REVIEWING: return "settled_reviewing";
    case LeviState.NEEDS_INFORMATION: return "settled_needs_info";
    case LeviState.WAITING_FOR_CONTRACTOR: return "settled_waiting";
    case LeviState.RECOMMENDATION_READY: return "settled_recommendation";
    case LeviState.RECOVERABLE_ERROR: return "settled_error";
    case LeviState.READY:
    default: return "settled_ready";
  }
};

const LeviWorkingEnvironment = ({
  viewModel,
  visualRegion = null,
  jobContextRegion = null,
  interactionRegion = null,
  smartRedirectRegion = null,
  reasoningRegion = null,
  recommendationRegion = null,
  hideIdentity = false,
}) => {
  const { t } = useLang();
  const reducedMotion = usePrefersReducedMotion();

  const state = useMemo(() => deriveLeviState(viewModel || {}), [viewModel]);
  const statusCaptionKey = statusCaptionKeyFor(state);
  const identityTitle = t("levi_estimate.working_env_identity_title");
  const visualAlt = t("levi_estimate.working_env_visual_alt");
  const statusCaption = t(statusCaptionKey);

  // The reasoning region already announces loading (aria-live polite)
  // and error (assertive) via the locked LeviVisionPanel. Silencing
  // this region's polite live-region in those two states avoids
  // duplicate screen-reader announcements.
  const captionAriaLive =
    state === LeviState.REVIEWING || state === LeviState.RECOVERABLE_ERROR
      ? "off"
      : "polite";

  // Resolve the visual region. If the host page passes a render
  // function, invoke it with the token + reduced-motion signal so
  // future visual implementations can react without any change here.
  // The working environment owns only the placement of the visual
  // inside the header flex layout; it does not dictate size, aspect
  // ratio, clipping, framing, background, alignment, animation
  // system, or rendering technology. Any React node — image,
  // animated component, Rive, Lottie, video, Canvas, WebGL,
  // Three.js, or a future visualization — may be supplied.
  const resolvedVisual =
    typeof visualRegion === "function"
      ? visualRegion({
          visualToken: stateToVisualToken(state),
          reducedMotion,
          accessibleName: visualAlt,
        })
      : visualRegion;

  return (
    <section
      aria-labelledby="levi-working-environment-title"
      data-testid="levi-working-environment"
      data-levi-state={state}
      className="space-y-6"
    >
      {!hideIdentity && (
      <header
        className="jc-card p-5 flex items-start gap-4"
        data-testid="levi-working-environment-header"
      >
        {resolvedVisual !== null && resolvedVisual !== undefined && (
          <div
            className="shrink-0"
            data-testid="levi-working-environment-visual"
          >
            {resolvedVisual}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2
            id="levi-working-environment-title"
            className="font-display text-lg leading-snug"
            data-testid="levi-working-environment-identity"
          >
            {identityTitle}
          </h2>
          <p
            className="text-sm text-[var(--jc-text-soft)] mt-1 break-words"
            role="status"
            aria-live={captionAriaLive}
            data-testid="levi-working-environment-status"
            data-status-key={statusCaptionKey}
          >
            {statusCaption}
          </p>
        </div>
      </header>
      )}

      {jobContextRegion ? (
        <div data-testid="levi-working-environment-job-context">
          {jobContextRegion}
        </div>
      ) : null}

      {interactionRegion ? (
        <div data-testid="levi-working-environment-interaction">
          {interactionRegion}
        </div>
      ) : null}

      {smartRedirectRegion ? (
        <div data-testid="levi-working-environment-smart-redirect">
          {smartRedirectRegion}
        </div>
      ) : null}

      {reasoningRegion ? (
        <div data-testid="levi-working-environment-reasoning">
          {reasoningRegion}
        </div>
      ) : null}

      {recommendationRegion ? (
        <div data-testid="levi-working-environment-recommendation">
          {recommendationRegion}
        </div>
      ) : null}
    </section>
  );
};

export { LeviWorkingEnvironment };
export default LeviWorkingEnvironment;
